import React from 'react';
import { Modal, Avatar, Divider } from 'antd';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import InfoText from '../../components/atoms/InfoText';
import TitleText from '../../components/atoms/TitleText';
import LoaderSpin from '../../components/atoms/LoaderSpin';
import MessageText from '../chat/components/atoms/MessageText';
import MessageImages from '../chat/components/atoms/MessageImages';
import MessageFiles from '../chat/components/atoms/MessageFiles';
import { useGetSupportChatHistoryQuery } from '../../store/services/SupportService';
import { RootState } from '../../store/store';
import { formatDate } from '../../_utils/date';
import Helpers from '../../lib/helpers';

interface ChatHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  roomId: string;
}

export const ChatHistoryModal: React.FC<ChatHistoryModalProps> = ({
  isOpen,
  onClose,
  roomId,
}) => {
  const { t, i18n } = useTranslation();
  const user = useSelector((state: RootState) => state.auth.user);
  const userId = user?.adminData?.id;

  const { data, isLoading, isError } = useGetSupportChatHistoryQuery(
    { roomId, limit: 50, page: 0 },
    { skip: !isOpen || !roomId }
  );

  const chatGroups = data?.payload?.messagesGrouping ?? [];

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={false}
      centered
      width={640}
      destroyOnClose
      className="[&_.ant-modal-body]:!max-h-[70vh] [&_.ant-modal-body]:!overflow-y-auto [&_.ant-modal-content]:!p-6 [&_.ant-modal-content]:!bg-light-surface-primary dark:[&_.ant-modal-content]:!bg-dark-surface-primary"
      data-testid="ticket-chat-history-modal"
      title={
        <div className="space-y-1" data-testid="ticket-chat-history-header">
          <TitleText
            text={t('support.Ticket_chat_history')}
            size="lg"
            className="!font-bold text-light-primary dark:text-dark-primary"
            data-testid="ticket-chat-history-title"
          />
        </div>
      }
    >
      {/* No chat room created */}
      {!roomId && (
        <div
          className="py-10 text-center flex justify-center items-center"
          data-testid="ticket-chat-history-no-room"
        >
          <InfoText
            text={t('support.no_chat_history')}
            className="!text-center text-light-text-secondary dark:text-dark-text-secondary"
            data-testid="ticket-chat-history-no-room-text"
          />
        </div>
      )}

      {/* Loading */}
      {Boolean(roomId && isLoading) && (
        <div
          className="flex items-center justify-center py-10"
          data-testid="ticket-chat-history-loading"
        >
          <LoaderSpin />
        </div>
      )}

      {/* Error */}
      {Boolean(roomId && isError) && (
        <div
          className="py-10 text-center flex justify-center items-center"
          data-testid="ticket-chat-history-error"
        >
          <InfoText
            text={t('support.somethingWentWrong')}
            className="!text-center text-light-warning dark:text-dark-warning"
            data-testid="ticket-chat-history-error-text"
          />
        </div>
      )}

      {/* Empty */}
      {Boolean(roomId && !isLoading && !isError && chatGroups.length === 0) && (
        <div
          className="py-10 text-center flex justify-center items-center"
          data-testid="ticket-chat-history-empty"
        >
          <InfoText
            text={t('support.no_chat_history')}
            className="!text-center text-light-text-secondary dark:text-dark-text-secondary"
            data-testid="ticket-chat-history-empty-text"
          />
        </div>
      )}

      {/* Chat Messages */}
      {Boolean(roomId && chatGroups.length > 0) && (
        <div
          className="py-2"
          data-testid="ticket-chat-history-messages-container"
        >
          {chatGroups.map((section, groupIndex) => (
            <div
              key={section.date || `group-${groupIndex}`}
              className="mb-4"
              data-testid={`ticket-chat-history-group-${section.date || groupIndex}`}
            >
              {Boolean(section.date) && (
                <Divider
                  className="border-light-input-border dark:border-dark-input-border my-4"
                  data-testid={`ticket-chat-history-date-divider-${section.date || groupIndex}`}
                >
                  <InfoText
                    text={formatDate(section.date, i18n?.language, t)}
                    className="text-xs !font-medium text-light-text-secondary dark:text-dark-text-secondary px-2"
                    data-testid={`ticket-chat-history-date-text-${section.date || groupIndex}`}
                  />
                </Divider>
              )}

              {/* Messages list */}
              <div
                className="space-y-3"
                data-testid={`ticket-chat-history-group-messages-${section.date || groupIndex}`}
              >
                {section.messages.map((msg) => {
                  const isSender =
                    msg.sender?.type === 'INTERNAL_USER' ||
                    (Boolean(userId) &&
                      String(msg.sender?.id) === String(userId));

                  const hasText = Boolean(msg.text?.trim());
                  const hasFiles = Boolean(msg.files?.length);
                  const hasImages = Boolean(msg.images?.length);

                  return (
                    <div
                      key={msg.id}
                      className={cn('flex gap-2.5 my-2', {
                        'justify-end': isSender,
                        'justify-start': !isSender,
                      })}
                      data-testid={`ticket-chat-history-message-item-${msg.id}`}
                    >
                      <div
                        className={cn('flex gap-2.5 max-w-[85%] sm:max-w-[75%]', {
                          'flex-row-reverse': isSender,
                          'flex-row': !isSender,
                        })}
                        data-testid={`ticket-chat-history-message-inner-${msg.id}`}
                      >
                        {/* Avatar */}
                        <div
                          className="min-w-fit flex-shrink-0"
                          data-testid={`ticket-chat-history-avatar-wrapper-${msg.id}`}
                        >
                          <Avatar
                            src={msg.sender?.image || undefined}
                            size={40}
                            className={cn('border-2 overflow-hidden', {
                              'border-light-success dark:border-dark-button-bg-primary':
                                isSender,
                              'border-light-input-border dark:border-dark-input-border':
                                !isSender,
                            })}
                            data-testid={`ticket-chat-history-avatar-${msg.id}`}
                          >
                            {msg.sender?.name?.charAt(0)?.toUpperCase()}
                          </Avatar>
                        </div>

                        {/* Content */}
                        <div
                          className={cn('flex flex-col gap-1 min-w-0', {
                            'items-end': isSender,
                            'items-start': !isSender,
                          })}
                          data-testid={`ticket-chat-history-content-${msg.id}`}
                        >
                          {/* Sender name & Type tag */}
                          <div
                            className={cn('flex items-center gap-1.5', {
                              'flex-row-reverse': isSender,
                              'flex-row': !isSender,
                            })}
                            data-testid={`ticket-chat-history-sender-header-${msg.id}`}
                          >
                            <TitleText
                              size="sm"
                              text={msg.sender?.name || (isSender ? 'Admin' : 'Customer')}
                              className="!font-semibold text-light-primary dark:text-dark-primary"
                              data-testid={`ticket-chat-history-sender-name-${msg.id}`}
                            />
                            {Boolean(msg.sender?.type) && (
                              <InfoText
                                text={`(${msg.sender.type?.replace(/_/g, ' ').toLowerCase()})`}
                                className="capitalize text-xs text-light-text-secondary dark:text-dark-text-secondary"
                                data-testid={`ticket-chat-history-sender-type-${msg.id}`}
                              />
                            )}
                          </div>

                          {/* Bubble containing text, files, and images */}
                          {(hasText || hasFiles || hasImages) && (
                            <div
                              className={cn(
                                'flex flex-col gap-2 rounded-2xl p-2.5 sm:p-3 w-fit max-w-full break-words shadow-sm border text-start',
                                {
                                  'rounded-tr-none bg-blue-50/70 dark:bg-blue-950/40 border-blue-200/50 dark:border-blue-900/50 text-light-primary dark:text-dark-primary':
                                    isSender,
                                  'rounded-tl-none bg-soft-background dark:bg-dark-surface-primary border-light-input-border/60 dark:border-dark-input-border/60 text-light-primary dark:text-dark-primary':
                                    !isSender,
                                }
                              )}
                              data-testid={`ticket-chat-history-bubble-${msg.id}`}
                            >
                              {hasText && <MessageText text={msg.text} />}
                              {hasFiles && <MessageFiles files={msg.files} />}
                              {hasImages && (
                                <MessageImages
                                  myMessage={isSender}
                                  images={msg.images}
                                />
                              )}
                            </div>
                          )}

                          {/* Timestamp */}
                          <div
                            className={cn(
                              'flex items-center text-xs text-light-text-secondary dark:text-dark-text-secondary mt-0.5',
                              {
                                'justify-end': isSender,
                                'justify-start': !isSender,
                              }
                            )}
                            data-testid={`ticket-chat-history-status-${msg.id}`}
                          >
                            <InfoText
                              text={Helpers.formatTime(msg.timestamp)}
                              className="text-xs text-light-text-secondary dark:text-dark-text-secondary"
                              data-testid={`ticket-chat-history-timestamp-${msg.id}`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};
